import { AttributeSpec, Node, Schema as PMSchema } from "prosemirror-model";

const textblockAttributes: Record<string, AttributeSpec> = {
  align: {
    default: "left",
    validate: (value) => {
      if (typeof value !== "string" || !/^(left|right|center)$/.test(value))
        throw new RangeError(`Invalid value for attribute align (${value})`);
    },
  },
  indent: {
    default: 0,
    validate: (value) => {
      if (typeof value !== "number" || value < 0 || Math.floor(value) !== value || value > 8)
        throw new RangeError(`Invalid value for attribute indent (${value})`);
    },
  },
};

export const schema = new PMSchema({
  nodes: {
    doc: {
      content: "block+",
    },
    paragraph: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["p", domAttributes(node), 0],
      group: "block",
      parseDOM: [{ tag: "p", getAttrs: parseAttributes }],
    },
    bullet_list_item: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["li", domAttributes(node, "bullet-list"), 0],
      parseDOM: [{ tag: "li.bullet-list", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
    },
    ordered_list_item: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["li", domAttributes(node, "ordered-list"), 0],
      parseDOM: [{ tag: "li.ordered-list", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
    },
    quote: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["blockquote", domAttributes(node), 0],
      parseDOM: [{ tag: "blockquote", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
    },
    code_block: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["pre", domAttributes(node, "code-block"), 0],
      parseDOM: [{ tag: "pre.code-block", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
      code: true, // no smart quotes in code blocks
    },
    heading: {
      content: "inline*",
      attrs: {
        ...textblockAttributes,
        level: {
          default: 1,
          validate: (value) => {
            if (typeof value !== "number" || value < 1 || Math.floor(value) !== value || value > 6)
              throw new RangeError(`Invalid value for attribute level (${value})`);
          },
        },
      },
      toDOM: (node) => [`h${node.attrs.level}`, domAttributes(node), 0],
      parseDOM: [
        { tag: "h1", getAttrs: parseHeadingAttributes(1) },
        { tag: "h2", getAttrs: parseHeadingAttributes(2) },
        { tag: "h3", getAttrs: parseHeadingAttributes(3) },
        { tag: "h4", getAttrs: parseHeadingAttributes(4) },
        { tag: "h5", getAttrs: parseHeadingAttributes(5) },
        { tag: "h6", getAttrs: parseHeadingAttributes(6) },
      ],
      group: "block",
      defining: true,
    },
    image: {
      inline: true,
      group: "inline",
      attrs: {
        src: { validate: "string" },
        width: { default: null, validate: "number|null" },
      },
      toDOM: (node) => [
        "img",
        {
          src: node.attrs.src,
          style: node.attrs.width ? `width: ${node.attrs.width}px` : null,
          class: "inline-block align-bottom",
        },
      ],
      parseDOM: [
        {
          tag: "img[src]",
          getAttrs: (elt: HTMLElement) => ({
            src: (elt as HTMLImageElement).src,
            width: elt.style.width ? parseInt(elt.style.width, 10) : null,
          }),
        },
      ],
    },
    formula: {
      inline: true,
      group: "inline",
      attrs: {
        formula: { validate: "string" },
      },
      toDOM: (node) => ["span", { class: "formula" }, node.attrs.formula],
      parseDOM: [
        {
          tag: "span.formula",
          getAttrs: (elt) => ({ formula: elt.textContent }),
        },
      ],
    },
    text: {
      inline: true,
      group: "inline",
    },
  },
  marks: {
    link: {
      attrs: {
        href: { validate: "string" },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs: (dom: HTMLElement) => ({ href: (dom as HTMLLinkElement).href }),
        },
      ],
      toDOM: (node) => [
        "a",
        { href: node.attrs.href, target: "_blank", rel: "noopener noreferrer" },
        0,
      ],
    },
    color: {
      attrs: {
        color: {
          validate: (value) => {
            if (typeof value !== "string" || !/^#[\da-f]{6}$/i.test(value))
              throw new RangeError(`Invalid value for attribute color (${value})`);
          },
        },
      },
      toDOM: (node) => ["span", { style: `color: ${node.attrs.color}` }, 0],
      parseDOM: [{ tag: "span", getAttrs: parseColorAttributes }],
    },
    italic: {
      parseDOM: [
        { tag: "i" },
        { tag: "em" },
        { style: "font-style=italic" },
        { style: "font-style=normal", clearMark: (m) => m.type.name === "italic" },
      ],
      toDOM() {
        return ["em", 0];
      },
    },
    bold: {
      parseDOM: [
        { tag: "strong" },
        { tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight !== "normal" && null },
        { style: "font-weight=400", clearMark: (m) => m.type.name === "bold" },
        {
          style: "font-weight",
          getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
        },
      ],
      toDOM() {
        return ["strong", 0];
      },
    },
    underline: {
      parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }],
      toDOM() {
        return ["u", 0];
      },
    },
  },
});

export type Schema = typeof schema;
type ExtractSchemaNodes<T> = T extends PMSchema<infer U> ? U : never;
export type SchemaNodes = ExtractSchemaNodes<Schema>;

function domAttributes(node: Node, baseClass?: string) {
  const cls = baseClass ? [baseClass] : [];
  const result: Record<string, string> = {};
  if (node.attrs.align === "center") cls.push("text-center");
  if (node.attrs.align === "right") cls.push("text-right");
  if (node.attrs.indent) cls.push(`indent-${node.attrs.indent}`);
  if (cls.length) result.class = cls.join(" ");
  return result;
}

function parseAttributes(elt: HTMLElement) {
  const attrs: Record<string, unknown> = { indent: 0, align: "left" };
  for (let i = 0; i < elt.classList.length; i += 1) {
    const cls = elt.classList[i];
    const m = /^indent-([1-9])$/.exec(cls);
    if (cls === "text-right") attrs.align = "right";
    else if (cls === "text-center") attrs.align = "center";
    else if (m) attrs.indent = +m[1];
  }
  return attrs;
}

function parseHeadingAttributes(level: number) {
  return (elt: HTMLElement) => {
    const attrs = parseAttributes(elt);
    attrs.level = level;
    return attrs;
  };
}

function parseColorAttributes(elt: HTMLElement) {
  const m = /^#([\da-f]{3,8})|^rgba?\((\d+), ?(\d+), ?(\d+)/i.exec(elt.style.color);
  if (!m) return false;
  const [, hex, red, green, blue] = m;
  let r;
  let g;
  let b;
  if (hex && hex.length >= 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (hex) {
    r = parseInt(hex[0], 16) * 17;
    g = parseInt(hex[1], 16) * 17;
    b = parseInt(hex[2], 16) * 17;
  } else {
    r = parseInt(red, 10);
    g = parseInt(green, 10);
    b = parseInt(blue, 10);
  }
  const n = (value: number) => value.toString(16).toUpperCase().padStart(2, "0");
  return { color: `#${n(r)}${n(g)}${n(b)}` };
}
